import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { DayName, UitkDay } from './uitk-day.esm.js';
import styles from './uitk-date-picker.css';
var getClassNameList = classNames.bind(styles);
var Month = /** @class */function (_super) {
  __extends(Month, _super);
  function Month(props) {
    var _this = _super.call(this, props) || this;
    // eslint-disable-next-line react/destructuring-assignment
    _this.date = new Date(_this.props.year, _this.props.month, 1, 12);
    _this.monthRef = React.createRef();
    _this.state = {
      localizedText: {
        selectDone: function (dateText) {
          return props.l10n.formatText('datePicker.selectDone.liveAnnounce', dateText, props.buttonText);
        },
        selectEndDate: function (dateText) {
          return props.l10n.formatText('datePicker.selectEndDate.liveAnnounce', dateText);
        }
      }
    };
    _this.firstDayOfWeekOffset = _this.getFirstDayOfWeekOffset(props.firstDayOfWeek);
    _this.onSelectDate = _this.onSelectDate.bind(_this);
    return _this;
  }
  Month.prototype.componentDidMount = function () {
    var _a = this.props,
      isDialog = _a.isDialog,
      onScrollHandler = _a.onScrollHandler;
    if (isDialog && onScrollHandler) {
      var today = new Date();
      var date_1 = this.date < today ? today : this.date;
      var options = {
        root: null,
        rootMargin: '0px',
        threshold: 1
      };
      var observer = new IntersectionObserver(function (_a) {
        var entry = _a[0];
        if (onScrollHandler && entry.intersectionRatio === 1) {
          onScrollHandler(date_1);
        }
      }, options);
      if (this.monthRef.current) {
        observer.observe(this.monthRef.current);
      }
    }
  };
  Month.prototype.shouldComponentUpdate = function (nextProps) {
    var _a = this.props,
      datePickerContent = _a.datePickerContent,
      selection = _a.selection,
      maxDate = _a.maxDate,
      minDate = _a.minDate;
    var nextSelection = nextProps.selection,
      nextMaxDate = nextProps.maxDate,
      nextMinDate = nextProps.minDate;
    // If available dates have changed
    if (maxDate !== nextMaxDate || minDate !== nextMinDate) {
      return true;
    }
    // If Selection has changed
    if (nextSelection) {
      return true;
    }
    if (!!nextSelection !== !!selection) {
      return true;
    }
    // If datePickerContent has changed
    if (nextProps.datePickerContent !== datePickerContent) {
      return true;
    }
    return false;
  };
  Month.prototype.onSelectDate = function (event, date) {
    var onSelectDate = this.props.onSelectDate;
    var localizedText = this.state.localizedText;
    if (onSelectDate) {
      onSelectDate(event, date, localizedText);
    }
  };
  Month.prototype.getFirstDayOfWeekOffset = function (day) {
    if (Object.values(DayName).indexOf(day) === -1 && day !== undefined) {
      console.error("Invalid argument provided to UITK DatePicker's firstDayOfWeek prop. Utilize Day enum from module.");
    }
    var offset = day || 0;
    return offset;
  };
  Month.prototype.generateClassNames = function (date) {
    var selection = this.props.selection;
    var selectionStart = selection ? selection.start : undefined;
    var selectionEnd = selection ? selection.end : undefined;
    var endSelected = selectionEnd && selectionEnd.getTime() === date.getTime();
    var startSelected = selectionStart && selectionStart.getTime() === date.getTime();
    var isFirstDate = date.getDate() === 1;
    var isLastDate = date.getDate() >= 28 && date.getDate() === new Date(date.getYear(), date.getMonth() + 1, 0).getDate();
    var cssClassNames = getClassNameList('uitk-date-picker-day-number', {
      end: endSelected && selectionStart,
      endSelected: endSelected,
      start: startSelected && selectionEnd,
      startSelected: startSelected,
      'uitk-date-picker-first-of-month': isFirstDate,
      'uitk-date-picker-last-of-month': isLastDate
    });
    return cssClassNames;
  };
  Month.prototype.renderDays = function () {
    var today = new Date();
    var days = [];
    for (var i = 0; i < 7; i++) {
      var _a = this.props,
        l10n = _a.l10n,
        month = _a.month;
      var dayOfWeekDate = new Date(today.setDate(today.getDate() - today.getDay() + i + this.firstDayOfWeekOffset));
      var dayName = l10n.formatDate(dayOfWeekDate, {
        dateFormat: 'day-of-week-full',
        raw: 'EEEE'
      });
      var dayInitial = l10n.formatDate(dayOfWeekDate, {
        dateFormat: 'day-of-week-short',
        raw: 'EEEEE'
      });
      days.push(React.createElement("th", {
        key: "".concat(month, "-weekday").concat(dayName),
        className: getClassNameList('uitk-date-picker-day-name'),
        scope: "col"
      }, React.createElement("span", {
        className: getClassNameList('is-visually-hidden')
      }, dayName), React.createElement("span", {
        "aria-hidden": "true"
      }, dayInitial)));
    }
    return days;
  };
  Month.prototype.renderWeeks = function () {
    var _a = this.props,
      maxDate = _a.maxDate,
      minDate = _a.minDate,
      l10n = _a.l10n,
      month = _a.month,
      year = _a.year;
    var minimumDate = minDate || new Date(1970, 0, 1, 12);
    var maximumDate = maxDate || new Date(9999, 0, 1, 12);
    var minDateTime = minimumDate.getTime();
    var maxDateTime = maximumDate.getTime();
    var weeks = [];
    var date = new Date(year, month, 1, 12);
    var leadingBlanks = date.getDay() - this.firstDayOfWeekOffset;
    if (leadingBlanks < 0) {
      leadingBlanks = 7 + leadingBlanks;
    }
    var _b = this.props,
      selection = _b.selection,
      datePickerContent = _b.datePickerContent,
      getHolidayName = _b.getHolidayName;
    var selectionStart = selection ? selection.start : undefined;
    var selectionEnd = selection ? selection.end : undefined;
    var onSelectDate = this.onSelectDate;
    while (date.getMonth() === month) {
      var dateJsx = [];
      // We iterate every 7 days to build a row at a time
      for (var weekDate = 1; weekDate <= 7; weekDate += 1) {
        var monthChanged = date.getMonth() !== month;
        // First row can have some leading blank cells
        if (leadingBlanks > 0) {
          dateJsx.push(React.createElement("td", {
            "aria-hidden": true,
            className: getClassNameList('uitk-date-picker-day-number'),
            key: "".concat(month, "-leading").concat(weekDate),
            colSpan: leadingBlanks
          }));
          weekDate = leadingBlanks;
          leadingBlanks = 0;
        } else if (!monthChanged) {
          var formattedISODate = l10n.formatDate(date, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd'
          });
          var disabled = date.getTime() < minDateTime || date.getTime() > maxDateTime;
          // Valid day cells
          dateJsx.push(React.createElement("td", {
            className: this.generateClassNames(date),
            key: "".concat(month, "-").concat(date.getDate())
          }, React.createElement(UitkDay, {
            content: datePickerContent && datePickerContent[formattedISODate],
            date: date,
            disabled: disabled,
            formattedDate: String(date.getDate()),
            formattedFriendlyDate: l10n.formatDate(date, {
              dateFormat: 'long',
              skeleton: 'yMMMd'
            }),
            holidayName: getHolidayName && getHolidayName(date),
            onSelectDate: onSelectDate,
            selectionStart: selectionStart,
            selectionEnd: selectionEnd,
            selection: selection
          })));
          date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 12);
        }
      }
      weeks.push(React.createElement("tr", {
        key: "".concat(month, "-weekEnding-").concat(date.getTime())
      }, dateJsx));
    }
    return weeks;
  };
  Month.prototype.render = function () {
    var l10n = this.props.l10n;
    var yearMonthHeading = l10n.formatDate(this.date, {
      dateFormat: 'month-year',
      skeleton: 'yMMMM'
    });
    return React.createElement("div", {
      className: getClassNameList('uitk-date-picker-month'),
      "data-stid": "date-picker-month",
      ref: this.monthRef
    }, React.createElement("h2", {
      className: getClassNameList('uitk-date-picker-month-name', 'uitk-type-medium')
    }, yearMonthHeading), React.createElement("table", {
      className: getClassNameList('uitk-date-picker-weeks')
    }, React.createElement("thead", null, React.createElement("tr", null, this.renderDays())), React.createElement("tbody", null, this.renderWeeks())));
  };
  Month.defaultProps = {
    firstDayOfWeek: DayName.SUN
  };
  return Month;
}(React.Component);
var UitkMonth = function (props) {
  var l10n = useLocalization();
  return React.createElement(Month, __assign({
    l10n: l10n
  }, props));
};
UitkMonth.displayName = 'UitkMonth';
var UitkNewMonth = UitkMonth;
export { UitkMonth, UitkNewMonth };